import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { developerApi } from '../../api/developer.api';
import type {
  MiniAppResponseDto,
  CreateMiniAppDto,
  UpdateMiniAppDto,
  CategoryResponseDto,
  AppKeypair,
  AppDomainsResponseDto,
  DomainTicketDto,
} from '../../types';

export function useMiniAppCategories(options?: { enabled?: boolean }) {
  return useQuery<CategoryResponseDto[]>({
    queryKey: ['developer', 'categories'],
    queryFn: async () => {
      const { data } = await developerApi.get('/v1/miniapp/categories');
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.data?.items)) return data.data.items;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useDeveloperApps(
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    categoryId?: string;
    category?: string;
    platform?: string;
  },
  options?: { enabled?: boolean }
) {
  return useQuery<{ items: any[]; total: number }>({
    queryKey: ['developer', 'apps', params],
    queryFn: async () => {
      const cleanParams: Record<string, any> = {};
      if (params?.page && params.page > 0) cleanParams.page = params.page;
      if (params?.limit && params.limit > 0) cleanParams.limit = params.limit;
      if (params?.search && params.search.trim()) cleanParams.search = params.search.trim();
      if (params?.categoryId && params.categoryId !== 'ALL') {
        cleanParams.categoryId = params.categoryId;
      } else if (params?.category && params.category !== 'ALL') {
        cleanParams.categoryId = params.category;
      }
      if (params?.platform && params.platform !== 'ALL') cleanParams.platform = params.platform;

      const { data } = await developerApi.get('/v1/miniapp/apps', {
        params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
      });
      const raw = data.data || data;

      if (raw && Array.isArray(raw.items)) {
        const mappedItems = raw.items.map((item: any) => ({
          ...item,
          id: item.id,
          name: item.name,
          package_name: item.packageName || item.package_name,
          packageName: item.packageName || item.package_name,
          app_type: item.appType || item.category || 'MINI_APP',
          appType: item.appType || item.category || 'MINI_APP',
          category: item.category || item.appType,
          categoryId: item.categoryId,
          status: item.status || 'active',
          created_at: item.createdAt || new Date().toISOString(),
          updated_at: item.updatedAt || item.createdAt || new Date().toISOString(),
        }));
        return { items: mappedItems, total: raw.total ?? mappedItems.length };
      }

      if (Array.isArray(raw)) {
        const mappedItems = raw.map((item: any) => ({
          ...item,
          id: item.id,
          name: item.name,
          package_name: item.packageName || item.package_name,
          packageName: item.packageName || item.package_name,
          app_type: item.appType || item.category || 'MINI_APP',
          appType: item.appType || item.category || 'MINI_APP',
          category: item.category || item.appType,
          categoryId: item.categoryId,
          status: item.status || 'active',
          created_at: item.createdAt || new Date().toISOString(),
          updated_at: item.updatedAt || item.createdAt || new Date().toISOString(),
        }));
        return { items: mappedItems, total: mappedItems.length };
      }

      return { items: [], total: 0 };
    },
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useDeveloperApp(id: string) {
  return useQuery<any>({
    queryKey: ['developer', 'app', id],
    queryFn: async () => {
      const { data } = await developerApi.get(`/v1/miniapp/apps/${id}`);
      const raw = data.data || data;

      return {
        ...raw,
        id: raw.id,
        name: raw.name,
        package_name: raw.packageName || raw.package_name,
        packageName: raw.packageName || raw.package_name,
        app_type: raw.appType || raw.category || 'MINI_APP',
        appType: raw.appType || raw.category || 'MINI_APP',
        category: raw.category || raw.appType,
        categoryId: raw.categoryId,
        status: raw.status || 'active',
        description: raw.description,
        iconUrl: raw.iconUrl,
        imageUrl: raw.imageUrl,
        link: raw.link,
        domains: raw.domains || [],
        versions: raw.versions || [],
        created_at: raw.createdAt,
        updated_at: raw.updatedAt,
      };
    },
    enabled: !!id && id !== 'new' && id !== 'create',
    staleTime: 30_000,
  });
}

export function useCreateDeveloperApp() {
  const queryClient = useQueryClient();
  return useMutation<MiniAppResponseDto, Error, CreateMiniAppDto>({
    mutationFn: async (payload) => {
      const formData = new FormData();
      if (payload.name) formData.append('name', payload.name);
      if (payload.description) formData.append('description', payload.description);
      if (payload.categoryId) formData.append('categoryId', payload.categoryId);
      if (payload.appType) formData.append('appType', payload.appType);
      if (payload.link) formData.append('link', payload.link);
      if (payload.packageName) formData.append('packageName', payload.packageName);
      if (payload.platform) formData.append('platform', payload.platform);
      if (payload.category && !payload.categoryId) formData.append('category', payload.category);
      if (payload.image) formData.append('image', payload.image);

      const { data } = await developerApi.post('/v1/miniapp/apps', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'apps'] });
    },
  });
}

export function useUpdateDeveloperAppMetadata() {
  const queryClient = useQueryClient();
  return useMutation<MiniAppResponseDto, Error, { id: string; data: UpdateMiniAppDto }>({
    mutationFn: async ({ id, data: payload }) => {
      const formData = new FormData();
      if (payload.name !== undefined) formData.append('name', payload.name);
      if (payload.description !== undefined) formData.append('description', payload.description);
      if (payload.categoryId !== undefined) formData.append('categoryId', payload.categoryId);
      if (payload.link !== undefined) formData.append('link', payload.link);
      if (payload.platform !== undefined) formData.append('platform', payload.platform);
      if (payload.image) formData.append('image', payload.image);

      const { data } = await developerApi.patch(`/v1/miniapp/apps/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data || data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'app', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'apps'] });
    },
  });
}

export function useDeleteDeveloperApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await developerApi.delete(`/v1/miniapp/apps/${id}`);
      return data.data || data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'apps'] });
    },
  });
}

// Keypair generation & rotation helper
export function useRotateDeveloperAppKeys() {
  const queryClient = useQueryClient();
  return useMutation<AppKeypair, Error, string>({
    mutationFn: async (id: string) => {
      // Create new keypair or rotate
      const { data } = await developerApi.post(`/v1/miniapp/apps/${id}/keypairs`, {});
      return data.data || data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'apps', id, 'keypairs'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'app', id] });
    },
  });
}

export function useDeveloperAppDomains(id: string) {
  return useQuery<AppDomainsResponseDto>({
    queryKey: ['developer', 'app', id, 'domains'],
    queryFn: async () => {
      const { data } = await developerApi.get(`/v1/miniapp/apps/${id}/domains`);
      return data.data || data;
    },
    enabled: !!id && id !== 'new' && id !== 'create',
    staleTime: 30_000,
  });
}

export function useDeveloperAppDomainTickets(id: string) {
  return useQuery<DomainTicketDto[]>({
    queryKey: ['developer', 'app', id, 'domains', 'tickets'],
    queryFn: async () => {
      const { data } = await developerApi.get(`/v1/miniapp/apps/${id}/domains/tickets`);
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.data?.items)) return data.data.items;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    },
    enabled: !!id && id !== 'new' && id !== 'create',
    staleTime: 30_000,
  });
}

export function useConfigureDeveloperAppDomains() {
  const queryClient = useQueryClient();
  return useMutation<
    MiniAppResponseDto,
    Error,
    { id: string; domains: Array<{ domain: string; isPrimary: boolean; description?: string | null; purpose?: string | null }> }
  >({
    mutationFn: async ({ id, domains }) => {
      const sanitizedDomains = domains.map((d) => ({
        ...d,
        domain: d.domain.replace(/^[a-zA-Z]+:\/\//, '').replace(/\/.*$/, '').trim(),
      }));
      const { data } = await developerApi.patch(`/v1/miniapp/apps/${id}/domains`, { domains: sanitizedDomains });
      return data.data || data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'app', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'app', variables.id, 'domains'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'app', variables.id, 'domains', 'tickets'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'app', variables.id, 'ticket'] });
    },
  });
}

export function useSubmitDeveloperApp() {
  const queryClient = useQueryClient();
  return useMutation<any, Error, string>({
    mutationFn: async (appId: string) => {
      const { data } = await developerApi.post(`/v1/miniapp/apps/${appId}/submit`);
      return data.data || data;
    },
    onSuccess: (_, appId) => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'app', appId] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'apps'] });
      queryClient.invalidateQueries({ queryKey: ['developer', 'app', appId, 'ticket'] });
    },
  });
}

export function useDeveloperAppTicket(appId: string) {
  return useQuery<any>({
    queryKey: ['developer', 'app', appId, 'ticket'],
    queryFn: async () => {
      try {
        const { data } = await developerApi.get(`/v1/miniapp/apps/${appId}/ticket`);
        return data.data || data;
      } catch (err: any) {
        if (err.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!appId && appId !== 'new' && appId !== 'create',
    staleTime: 15_000,
    retry: false,
  });
}
